/**
 * Create / edit one dimension — three origins behind one origin picker.
 *
 * WHAT THE FORM IS ACTUALLY GUARDING
 * ----------------------------------
 * `dimensions.config_json` is free JSONB with no CHECK, so a misspelled
 * key stores cleanly, renders fine in every `SELECT`, and surfaces hours
 * later inside the loader. The backend parses it CLOSED-WORLD per origin
 * for exactly that reason (`schemas/dimensions.check_config_shape`), and
 * this form's whole job is to emit the keys that parser accepts:
 *
 *   column    → { source_column }
 *   mapping   → { match_column, master_collection_slug,
 *                 master_sheet_name, key_column, value_column }
 *   computed  → { sql_expression }
 *
 * `source_column` is NOT sent for mapping and computed: for those two it
 * names the column the ENRICHMENT writes, the server derives it as the
 * dimension key, and accepting it from a client would let a scope rule
 * on one column while the reload writes another.
 *
 * WHY THE COLUMN PICKERS HAVE AN "OTHER…" ESCAPE
 * ----------------------------------------------
 * The only column list this app has is `projects.data_source_schema` —
 * what the LAST RELOAD RECORDED. The backend validates against the LIVE
 * FILE, and the two disagree in ordinary situations (the data source
 * cleared while the file survives, hand DDL, a reload that never ran).
 * A `Select` built from the record alone would make a real column
 * unpickable; a bare text box would make every column a guess. So: the
 * recorded columns as options, plus one explicit escape that says the
 * name is not in the record. Neither invents anything.
 *
 * WHY THE MAPPING FIELDS ARE TYPED AND NOT PICKED
 * -----------------------------------------------
 * There is no builder-side endpoint that lists a project's Masters —
 * `grep -rn "master_data" apps/ai-reporting-builder/src` finds comments
 * and no reader outside the J2 enrichment. Rendering a picker would mean
 * inventing the list. What the backend does instead is enumerate the
 * real options IN ITS REFUSAL ("project 'hp' has no master collection
 * 'x' (it has: [...])", "master 'x' has no tab 'y' (tabs: [...])"), so
 * the 400 is rendered verbatim and is the thing that teaches the names.
 * When a masters endpoint lands, these three inputs become selects and
 * nothing else here changes.
 */

import {
  Button,
  Checkbox,
  CodeEditor,
  Input,
  Modal,
  Select,
  type SelectOption,
} from "@omnitok/ui";
import { Callout, FormField } from "@pervasivemind/admin-ui";
import {
  toLocalized,
  type LocalizedString,
} from "@pervasivemind/dashboard-render";
import { useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import {
  DIMENSION_USABLE_AS,
  dimensionsApi,
  flattenRawColumns,
  rawTableName,
  type DimensionOrigin,
  type DimensionOut,
  type DimensionUsableAs,
} from "@/lib/dimensions-api";
import { lintColumnReferences } from "@/lib/sql-lint";
import { BilingualInput } from "@/pages/builder/components/BilingualInput";
import type { Project } from "@/types";

/** Mirrors `schemas/dimensions.DIMENSION_KEY_RE`. Checked here so the
 *  ordinary typo gets a sentence instead of a pydantic 422, whose
 *  `detail` is a LIST and therefore reaches `extractMessage`'s fallback
 *  as the bare words "Unprocessable Entity" (`api.ts:280-291`). */
const KEY_RE = /^[a-z][a-z0-9_]{0,127}$/;

/** Sentinel option of a column picker: "the name is not in the recorded
 *  schema". A real column can never collide with it — a DuckDB column
 *  named ` custom` is not expressible. */
const CUSTOM = " custom";

interface ColumnPickerProps {
  label: string;
  help: string;
  otherLabel: string;
  customLabel: string;
  value: string;
  columns: string[];
  disabled?: boolean;
  onChange: (next: string) => void;
}

/**
 * A recorded-schema picker with an explicit way out.
 *
 * The escape is a visible option and not a silent free-text fallback:
 * the operator says "this column is not in the record", and the backend
 * — which reads the real file — is what decides whether it exists.
 *
 * `custom` is seeded once, so every call site KEYS this component on a
 * round the parent bumps AFTER it re-seeds `config` (`seedRound`), not
 * just on the dimension. The Modal unmounts its children on close, and
 * in the render that opens it `config` is still the previous one: a
 * picker seeded there would greet the dimension in the wrong mode (an
 * empty select over an unlisted column, or a stale free-text box).
 */
function ColumnPicker({
  label,
  help,
  otherLabel,
  customLabel,
  value,
  columns,
  disabled,
  onChange,
}: ColumnPickerProps): JSX.Element {
  const known = columns.includes(value);
  const [custom, setCustom] = useState(!known && value !== "");

  const options: SelectOption[] = [
    ...columns.map((c) => ({ value: c, label: c })),
    { value: CUSTOM, label: otherLabel },
  ];

  return (
    <div className="space-y-2">
      <Select
        label={label}
        helperText={help}
        fullWidth
        searchable
        disabled={disabled}
        value={custom ? CUSTOM : known ? value : ""}
        options={options}
        onChange={(e) => {
          const next = e.target.value;
          if (next === CUSTOM) {
            setCustom(true);
            onChange("");
          } else {
            setCustom(false);
            onChange(next);
          }
        }}
      />
      {custom && (
        <Input
          label={customLabel}
          fullWidth
          className="font-mono"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

export interface DimensionFormModalProps {
  open: boolean;
  project: Project;
  /** `null` creates. Editing keeps `origin` fixed — changing it would
   *  change what `source_column` MEANS while every scope rule kept
   *  pointing at the same key, so the backend does not accept it. */
  dimension: DimensionOut | null;
  /** Built once by the pane so the three origin labels have one
   *  definition; passing them down keeps the import graph acyclic. */
  originOptions: SelectOption[];
  onClose: () => void;
  onSaved: () => void;
}

interface ConfigDraft {
  source_column: string;
  match_column: string;
  master_collection_slug: string;
  master_sheet_name: string;
  key_column: string;
  value_column: string;
  sql_expression: string;
}

function emptyConfig(): ConfigDraft {
  return {
    source_column: "",
    match_column: "",
    master_collection_slug: "",
    master_sheet_name: "",
    key_column: "",
    value_column: "",
    sql_expression: "",
  };
}

function configFrom(row: DimensionOut | null): ConfigDraft {
  const base = emptyConfig();
  if (row === null) return base;
  const raw = row.config;
  const pick = (name: keyof ConfigDraft): string => {
    const value = raw[name];
    return typeof value === "string" ? value : "";
  };
  return {
    source_column: pick("source_column"),
    match_column: pick("match_column"),
    master_collection_slug: pick("master_collection_slug"),
    master_sheet_name: pick("master_sheet_name"),
    key_column: pick("key_column"),
    value_column: pick("value_column"),
    sql_expression: pick("sql_expression"),
  };
}

export function DimensionFormModal({
  open,
  project,
  dimension,
  originOptions,
  onClose,
  onSaved,
}: DimensionFormModalProps): JSX.Element {
  const { t } = useT();
  const isEdit = dimension !== null;

  const [key, setKey] = useState("");
  const [label, setLabel] = useState<LocalizedString>({ en: "", es: "" });
  const [origin, setOrigin] = useState<DimensionOrigin>("column");
  const [usableAs, setUsableAs] = useState<DimensionUsableAs[]>([]);
  const [config, setConfig] = useState<ConfigDraft>(emptyConfig());
  const [seedRound, setSeedRound] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed on every open so a cancelled edit does not leak into the
  // next one. `open` is in the deps because the modal stays mounted.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    setKey(dimension?.key ?? "");
    setLabel(toLocalized(dimension?.label ?? { en: "", es: "" }));
    setOrigin(((dimension?.origin as DimensionOrigin) ?? "column"));
    setUsableAs((dimension?.usable_as ?? []) as DimensionUsableAs[]);
    setConfig(configFrom(dimension));
    // Remount the column pickers on the NEXT render: they seed
    // `custom` once, and in the open render `config` was still stale.
    setSeedRound((n) => n + 1);
  }, [open, dimension]);

  const columns = useMemo(
    () =>
      flattenRawColumns(project.data_source_schema, rawTableName(project)),
    [project],
  );

  const lint = useMemo(
    () => lintColumnReferences(config.sql_expression, columns),
    [config.sql_expression, columns],
  );

  const set = (patch: Partial<ConfigDraft>): void =>
    setConfig((current) => ({ ...current, ...patch }));

  /**
   * One half of the bilingual label, trimmed.
   *
   * `LocalizedString` makes each locale optional, and "the operator has
   * not typed it yet" and "they typed spaces" are the same state for
   * this form. Normalising the absent case to `""` is NOT a default
   * standing in for a value: the very next thing that happens to an
   * empty half is a refusal to submit, and the backend refuses it again
   * (`_validate_label`) because a half-filled `{en,es}` renders as a raw
   * key on one of the two screens.
   */
  const half = (locale: "en" | "es" | "pt"): string => (label[locale] ?? "").trim();
  /** EN and ES are required; PT travels only when someone wrote it
   *  (optional, decision D3 of the PT plan). */
  const labelPayload = (): Record<string, string> =>
    half("pt") === ""
      ? { en: half("en"), es: half("es") }
      : { en: half("en"), es: half("es"), pt: half("pt") };

  /** Only the two rules a pydantic 422 would answer unreadably. Every
   *  other rule is the backend's, and its 400 is the sentence that says
   *  how to fix it. */
  function localComplaint(): string | null {
    if (!KEY_RE.test(key.trim())) return t("dimensions.form.keyInvalid");
    if (half("en") === "" || half("es") === "") {
      return t("dimensions.form.labelIncomplete");
    }
    return null;
  }

  function payloadConfig(): Record<string, string> {
    if (origin === "column") {
      return { source_column: config.source_column.trim() };
    }
    if (origin === "mapping") {
      return {
        match_column: config.match_column.trim(),
        master_collection_slug: config.master_collection_slug.trim(),
        master_sheet_name: config.master_sheet_name.trim(),
        key_column: config.key_column.trim(),
        value_column: config.value_column.trim(),
      };
    }
    return { sql_expression: config.sql_expression };
  }

  const onSubmit = async (): Promise<void> => {
    const complaint = localComplaint();
    if (complaint !== null) {
      setError(complaint);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (dimension === null) {
        await dimensionsApi.create(project.slug, {
          key: key.trim(),
          label: labelPayload(),
          origin,
          usable_as: usableAs,
          config: payloadConfig(),
        });
      } else {
        await dimensionsApi.update(project.slug, dimension.id, {
          key: key.trim(),
          label: labelPayload(),
          usable_as: usableAs,
          config: payloadConfig(),
        });
      }
      onSaved();
    } catch (err) {
      // Rendered verbatim: a 400 from `service_dimensions` lists the
      // project's real columns, the project's real masters or the real
      // tabs. That enumeration is the only place those names appear in
      // this app, so paraphrasing it would delete the answer.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleUsable = (member: DimensionUsableAs, on: boolean): void =>
    setUsableAs((current) =>
      on
        ? [...new Set([...current, member])]
        : current.filter((v) => v !== member),
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? t("dimensions.form.editTitle") : t("dimensions.form.createTitle")}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" disabled={saving} onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={saving}
            disabled={saving}
            onClick={() => void onSubmit()}
          >
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error !== null && (
          <Callout tone="danger" title={t("dimensions.form.rejected")}>
            <span className="whitespace-pre-wrap">{error}</span>
          </Callout>
        )}

        <Input
          label={t("dimensions.form.keyLabel")}
          helperText={
            isEdit
              ? t("dimensions.form.keyHelpEdit")
              : t("dimensions.form.keyHelp")
          }
          fullWidth
          className="font-mono"
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />

        <FormField
          label={t("dimensions.form.labelLabel")}
          help={t("dimensions.form.labelHelp")}
        >
          <BilingualInput value={label} onChange={setLabel} />
        </FormField>

        <Select
          label={t("dimensions.form.originLabel")}
          helperText={
            isEdit
              ? t("dimensions.form.originHelpEdit")
              : t("dimensions.form.originHelp")
          }
          fullWidth
          disabled={isEdit}
          value={origin}
          options={originOptions}
          onChange={(e) => setOrigin(e.target.value as DimensionOrigin)}
        />

        <FormField
          label={t("dimensions.form.usableAsLabel")}
          help={
            usableAs.length === 0
              ? t("dimensions.usableAs.noneHint")
              : t("dimensions.form.usableAsHelp")
          }
        >
          <div className="flex flex-wrap gap-4">
            {DIMENSION_USABLE_AS.map((member) => (
              <Checkbox
                key={member}
                // Never pre-checked. The empty list is a real,
                // fail-closed declaration and defaulting it to "scope"
                // as a convenience would hand out access nobody asked
                // for.
                checked={usableAs.includes(member)}
                label={
                  member === "filter"
                    ? t("dimensions.usableAs.filter")
                    : t("dimensions.usableAs.scope")
                }
                onCheckedChange={(on) => toggleUsable(member, on)}
              />
            ))}
          </div>
        </FormField>

        {origin === "column" && (
          <ColumnPicker
            key={`source-${dimension?.id ?? "new"}-${seedRound}`}
            label={t("dimensions.form.column.sourceLabel")}
            help={t("dimensions.form.column.sourceHelp")}
            otherLabel={t("dimensions.form.column.other")}
            customLabel={t("dimensions.form.column.otherLabel")}
            value={config.source_column}
            columns={columns}
            disabled={saving}
            onChange={(next) => set({ source_column: next })}
          />
        )}

        {origin === "mapping" && (
          <div className="space-y-4">
            <ColumnPicker
              key={`match-${dimension?.id ?? "new"}-${seedRound}`}
              label={t("dimensions.form.mapping.matchLabel")}
              help={t("dimensions.form.mapping.matchHelp")}
              otherLabel={t("dimensions.form.column.other")}
              customLabel={t("dimensions.form.column.otherLabel")}
              value={config.match_column}
              columns={columns}
              disabled={saving}
              onChange={(next) => set({ match_column: next })}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label={t("dimensions.form.mapping.collectionLabel")}
                helperText={t("dimensions.form.mapping.collectionHelp")}
                fullWidth
                className="font-mono"
                value={config.master_collection_slug}
                onChange={(e) =>
                  set({ master_collection_slug: e.target.value })
                }
              />
              <Input
                label={t("dimensions.form.mapping.sheetLabel")}
                helperText={t("dimensions.form.mapping.sheetHelp")}
                fullWidth
                className="font-mono"
                value={config.master_sheet_name}
                onChange={(e) => set({ master_sheet_name: e.target.value })}
              />
              <Input
                label={t("dimensions.form.mapping.keyColumnLabel")}
                helperText={t("dimensions.form.mapping.keyColumnHelp")}
                fullWidth
                className="font-mono"
                value={config.key_column}
                onChange={(e) => set({ key_column: e.target.value })}
              />
              <Input
                label={t("dimensions.form.mapping.valueColumnLabel")}
                helperText={t("dimensions.form.mapping.valueColumnHelp")}
                fullWidth
                className="font-mono"
                error={
                  sameMappingColumns(config)
                    ? t("dimensions.form.mapping.sameColumn")
                    : undefined
                }
                value={config.value_column}
                onChange={(e) => set({ value_column: e.target.value })}
              />
            </div>
            <Callout tone="info" title={t("dimensions.form.mapping.noPickerTitle")}>
              {t("dimensions.form.mapping.noPicker")}
            </Callout>
          </div>
        )}

        {origin === "computed" && (
          <CodeEditor
            label={t("dimensions.form.computed.exprLabel")}
            helperText={t("dimensions.form.computed.exprHelp")}
            error={lintMessage(lint, t)}
            fullWidth
            language="sql"
            minHeight={140}
            showLineNumbers
            showCopyButton
            value={config.sql_expression}
            onValueChange={(next) => set({ sql_expression: next })}
          />
        )}
      </div>
    </Modal>
  );
}

/** Exported for the test: the one client-side rule of the mapping form
 *  that has a sentence of its own. Everything else is the backend's. */
export function sameMappingColumns(config: {
  key_column: string;
  value_column: string;
}): boolean {
  const key = config.key_column.trim();
  return key !== "" && key === config.value_column.trim();
}

function lintMessage(
  lint: ReturnType<typeof lintColumnReferences>,
  t: (key: string, params?: Record<string, string | number>) => string,
): string | undefined {
  if (lint.caseMismatches.length > 0) {
    const first = lint.caseMismatches[0];
    if (first !== undefined) {
      return t("dimensions.form.computed.caseMismatch", {
        used: first.used,
        suggest: first.suggest,
      });
    }
  }
  if (lint.unknownColumns.length > 0) {
    return t("dimensions.form.computed.unknownColumns", {
      columns: lint.unknownColumns.join(", "),
    });
  }
  return undefined;
}
